import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../ui/card";
import { Label } from "../../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Slider } from "../../../../ui/slider";
import { Button } from "../../../../ui/button";
import { Textarea } from "../../../../ui/textarea";
import { Switch } from "../../../../ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../ui/popover";
import { Checkbox } from "../../../../ui/checkbox";
import { Copy, Check, Star, BadgeCheck, ChevronsUpDown } from "lucide-react";
import { useToast } from "../../../../../hooks/use-toast";
import { supabase } from "../../../../../integrations/supabase/client";
import { formatDistanceToNowStrict } from "date-fns";

interface WidgetGeneratorProps {
  outlets: any[];
  selectedOutlets: string[];
  supabaseoutletIDs?: string[],
}

// Inline Google "G" SVG used in preview
const GoogleG = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Google" className="shrink-0">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.1-7.1 2.1-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.4l6.1 5C40.9 35.5 43.5 30.2 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

const Stars = ({ rating = 0, size = 14 }: { rating?: number; size?: number }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <svg
        key={i}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={i < rating ? "#FBBC04" : "#E5E7EB"}
        aria-hidden="true"
      >
        <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
  </div>
);

const initials = (name?: string | null) => {
  if (!name) return "A";
  return name.trim().charAt(0).toUpperCase();
};

const colorFor = (name?: string | null) => {
  const palette = ["#4F46E5", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];
  const s = (name || "A").charCodeAt(0);
  return palette[s % palette.length];
};

const PreviewCard = ({
  review,
  cardStyle = "rounded",
  theme = "light",
  fullWidth = false,
}: {
  review: any;
  cardStyle?: string;
  theme?: string;
  fullWidth?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const text = review.comment_text || "";
  const isLong = text.length > 180;
  const days = review.posted_at || review.created_at
    ? formatDistanceToNowStrict(new Date(review.posted_at || review.created_at), { addSuffix: true })
    : "";

  const isDark = theme === "dark";
  const radius = cardStyle === "minimal" ? "rounded-md" : "rounded-xl";
  const border =
    cardStyle === "bordered"
      ? isDark ? "border-2 border-gray-700" : "border-2 border-gray-300"
      : isDark ? "border border-gray-800" : "border border-gray-100";
  const bg = isDark ? "bg-gray-900 text-gray-100" : "bg-white";
  const shadow = cardStyle === "minimal" ? "" : "shadow-[0_1px_3px_rgba(0,0,0,0.08)]";

  return (
    <div className={`${bg} ${radius} ${shadow} ${border} p-4 ${fullWidth ? "w-full" : "w-[280px] shrink-0"} flex flex-col gap-3`}>
      <div className="flex items-start gap-3">
        <div className="relative">
          {review.author_avatar ? (
            <img src={review.author_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
              style={{ background: colorFor(review.author_name) }}
            >
              {initials(review.author_name)}
            </div>
          )}
          <div className="absolute -bottom-1 -left-1 bg-white rounded-full p-0.5 shadow">
            <GoogleG size={12} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="font-semibold text-sm text-gray-900 truncate">
              {review.author_name || "Anonymous"}
            </span>
            <BadgeCheck className="h-4 w-4 text-blue-500 fill-blue-500 stroke-white" />
          </div>
          <div className="text-xs text-gray-500">{days}</div>
        </div>
      </div>
      <Stars rating={review.rating || 0} size={14} />
      <p className={`text-sm text-gray-700 leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
        {text}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-blue-600 hover:underline self-start"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-auto">
        <GoogleG size={12} />
        <span>Posted on Google</span>
      </div>
    </div>
  );
};

export const WidgetGenerator = ({ outlets, selectedOutlets, supabaseoutletIDs }: WidgetGeneratorProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [displayMode, setDisplayMode] = useState<"single" | "multi-tab">("single");
  const [layout, setLayout] = useState("masonry");
  const [theme, setTheme] = useState("light");
  const [maxReviews, setMaxReviews] = useState([12]);
  const [cardStyle, setCardStyle] = useState("rounded");
  const [embedCode, setEmbedCode] = useState("");
  const [previewReviews, setPreviewReviews] = useState<any[]>([]);
  const [rows, setRows] = useState([3]);
  const [outletFilters, setOutletFilters] = useState<string[]>(["all"]);
  const [showHeader, setShowHeader] = useState(true);
  const [showWriteReview, setShowWriteReview] = useState(true);
  const [overall, setOverall] = useState({ avg_rating: 0, total: 0 });
  const [activeTab, setActiveTab] = useState<string>("all");
  const [outletSummaries, setOutletSummaries] = useState<any[]>([]);

  // Helpers
  const isAllOutlets =
    !outletFilters || outletFilters.length === 0 || outletFilters.includes("all");
  const selectedIds = isAllOutlets ? [] : outletFilters;
  // Single representative id for legacy paths (preview when 0 or 1 outlet)
  const outletFilter = isAllOutlets
    ? "all"
    : selectedIds.length === 1
    ? selectedIds[0]
    : "multi";

  // Sync internal filter with parent's selection (preserve multi-selection)
  useEffect(() => {
    const isAll =
      !selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all");
    if (isAll) setOutletFilters(["all"]);
    else setOutletFilters(selectedOutlets);
  }, [selectedOutlets]);

  // In multi-tab mode, the actual card layout still applies inside each tab.
  const effectiveLayout = layout;

  useEffect(() => {
    generateEmbedCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode, layout, theme, maxReviews, cardStyle, outletFilters, rows, showHeader, showWriteReview]);

  // Preview reviews depend on the active tab (in multi-tab mode) or outlet filter (single)
  useEffect(() => {
    fetchPreviewReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode, outletFilters, maxReviews, activeTab]);

  useEffect(() => {
    setActiveTab(isAllOutlets ? "all" : selectedIds[0] || "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletFilters]);

  const applyMultiFilter = (q: any, tabOverride?: string) => {
    // In multi-tab mode and a tab is active, scope to that tab only.
    if (displayMode === "multi-tab" && tabOverride && tabOverride !== "all") {
      return q.eq("outlet_id", tabOverride);
    }
    if (displayMode === "multi-tab" && tabOverride === "all" && !isAllOutlets) {
      // "All" tab in multi-tab still respects the selected subset
      return q.in("outlet_id", selectedIds);
    }
    if(displayMode === "multi-tab" && tabOverride === "all" && isAllOutlets) {
      return q.in("outlet_id", supabaseoutletIDs);
    }
    if (isAllOutlets) return q.in("outlet_id", supabaseoutletIDs);
    if (selectedIds.length === 1) return q.eq("outlet_id", selectedIds[0]);
    return q.in("outlet_id", selectedIds);
  };

  const fetchPreviewReviews = async () => {
    const tab = displayMode === "multi-tab" ? activeTab : undefined;

    // Featured first
    let q = supabase
      .from("reviews")
      .select("*, outlets(outlet_name, google_maps_url)")
      .eq("is_featured", true)
      .gte("rating", 4)
      .neq("platform", "epicreviews")
      .order("posted_at", { ascending: false, nullsFirst: false })
      .limit(maxReviews[0]);
    q = applyMultiFilter(q, tab);

    let { data } = await q;

    // Fallback to any positive reviews
    if (!data || data.length === 0) {
      let fb = supabase
        .from("reviews")
        .select("*, outlets(outlet_name, google_maps_url)")
        .gte("rating", 4)
        .neq("platform", "epicreviews")
        .order("posted_at", { ascending: false, nullsFirst: false })
        .limit(maxReviews[0]);
      fb = applyMultiFilter(fb, tab);
      const { data: fbData } = await fb;
      data = fbData || [];
    }

    setPreviewReviews(data || []);

    // Compute overall stats for preview header (for the active scope)
    let aggQuery = supabase.from("reviews").select("rating").not("rating", "is", null).neq("platform", "epicreviews");
    aggQuery = applyMultiFilter(aggQuery, tab);
    const { data: agg } = await aggQuery;
    const ratings = (agg || []).map((r: any) => r.rating).filter((n: any) => typeof n === "number");
    const total = ratings.length;
    const avg = total ? +(ratings.reduce((a, b) => a + b, 0) / total).toFixed(1) : 0;
    setOverall({ avg_rating: avg, total });

    // For multi-tab preview, compute per-outlet summaries (used for tab labels)
    if (displayMode === "multi-tab") {
      const summaryOutlets = isAllOutlets
        ? outlets
        : outlets.filter((o) => selectedIds.includes(o.id));
      const summaryIds = summaryOutlets.map((o) => o.id);
      let allRowsQuery = supabase
        .from("reviews")
        .select("outlet_id, rating")
        .not("rating", "is", null)
        .neq("platform", "epicreviews");
      if (summaryIds.length) allRowsQuery = allRowsQuery.in("outlet_id", summaryIds);
      const { data: allRows } = await allRowsQuery;
      const byOutlet: Record<string, { sum: number; count: number }> = {};
      (allRows || []).forEach((r: any) => {
        if (!r.outlet_id) return;
        if (!byOutlet[r.outlet_id]) byOutlet[r.outlet_id] = { sum: 0, count: 0 };
        byOutlet[r.outlet_id].sum += r.rating;
        byOutlet[r.outlet_id].count += 1;
      });
      setOutletSummaries(
        summaryOutlets.map((o) => {
          const a = byOutlet[o.id] || { sum: 0, count: 0 };
          return {
            id: o.id,
            outlet_name: o.outlet_name,
            google_maps_url: o.google_maps_url,
            avg_rating: a.count ? +(a.sum / a.count).toFixed(1) : 0,
            total: a.count,
          };
        })
      );
    }
  };

  const writeReviewUrl = useMemo(() => {
    if (isAllOutlets || selectedIds.length !== 1) return "";
    const o = outlets.find((x) => x.id === selectedIds[0]);
    return o?.google_maps_url || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletFilters, outlets]);

  const generateEmbedCode = () => {
    const projectId = "uthqjaflckswjjgokjdw";
    const baseUrl = `https://${projectId}.supabase.co/functions/v1/get-wall-of-love`;
    const outletsAttr = isAllOutlets ? supabaseoutletIDs.join(",") : selectedIds.join(",");

    const code = `<!-- EpicReview Wall of Love Widget -->
<div id="epic-walloflove"
  data-layout="${effectiveLayout}"
  data-mode="${displayMode === "multi-tab" ? "multi-tab" : "single"}"
  data-outlets="${outletsAttr}"
  data-theme="${theme}"
  data-limit="${maxReviews[0]}"
  data-style="${cardStyle}"
  data-rows="${rows[0]}"
  data-show-header="${showHeader}"
  data-show-write-review="${showWriteReview}"
></div>
<script>
(function() {
  var container = document.getElementById('epic-walloflove');
  var config = {
    layout: container.dataset.layout || 'masonry',
    mode: container.dataset.mode || 'single',
    outlets: container.dataset.outlets || 'all',
    theme: container.dataset.theme || 'light',
    limit: container.dataset.limit || '12',
    style: container.dataset.style || 'rounded',
    rows: parseInt(container.dataset.rows || '3'),
    showHeader: container.dataset.showHeader !== 'false',
    showWriteReview: container.dataset.showWriteReview !== 'false'
  };

  var GOOGLE_G = '<svg width="14" height="14" viewBox="0 0 48 48" style="flex-shrink:0"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.1-7.1 2.1-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.4l6.1 5C40.9 35.5 43.5 30.2 43.5 24c0-1.2-.1-2.3-.4-3.5z"/></svg>';
  var VERIFIED = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#1A73E8"><path d="M12 1l3.09 2.26L18.5 3l1.5 3.41L23 8l-1.41 3.09L23 14l-3 1.59L21.5 19l-3.41-.26L15 21l-3-2.5L9 21l-3.09-2.26L2.5 19l1.41-3.41L1 14l3-1.91L1 8l3.41-1.59L5.5 3l3.41.26L12 1z"/><path fill="#fff" d="M10.5 14.2l5.3-5.3-1.4-1.4-3.9 3.9-1.9-1.9-1.4 1.4z"/></svg>';
  function star(filled){return '<svg width="14" height="14" viewBox="0 0 24 24" fill="'+(filled?"#FBBC04":"#E5E7EB")+'"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';}
  function stars(n){var s='';for(var i=0;i<5;i++){s+=star(i<n);}return '<div style="display:flex;gap:2px">'+s+'</div>';}
  function timeAgo(iso){if(!iso)return'';var d=new Date(iso);var s=Math.floor((Date.now()-d.getTime())/1000);if(s<60)return s+'s ago';var m=Math.floor(s/60);if(m<60)return m+'m ago';var h=Math.floor(m/60);if(h<24)return h+'h ago';var dd=Math.floor(h/24);if(dd<30)return dd+' days ago';var mo=Math.floor(dd/30);if(mo<12)return mo+' months ago';return Math.floor(mo/12)+' years ago';}
  function initial(n){return (n||'A').trim().charAt(0).toUpperCase();}
  function colorFor(n){var p=['#4F46E5','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899'];return p[(n||'A').charCodeAt(0)%p.length];}
  function escapeHtml(s){return (s||'').replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];});}

  function cardHtml(r){
    var name=escapeHtml(r.author_name||'Anonymous');
    var avatar=r.author_avatar?'<img src="'+r.author_avatar+'" style="width:40px;height:40px;border-radius:50%;object-fit:cover" alt="">':'<div style="width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:600;background:'+colorFor(r.author_name)+'">'+initial(r.author_name)+'</div>';
    var body=escapeHtml(r.comment_text||'');
    return '<div class="epic-card">'+
      '<div class="epic-head">'+
        '<div class="epic-ava-wrap">'+avatar+'<div class="epic-g-badge">'+GOOGLE_G+'</div></div>'+
        '<div class="epic-meta"><div class="epic-name">'+name+VERIFIED+'</div><div class="epic-time">'+timeAgo(r.posted_at||r.created_at)+'</div></div>'+
      '</div>'+
      stars(r.rating||0)+
      '<p class="epic-body" data-full="'+encodeURIComponent(body)+'">'+body+'</p>'+
      (body.length>180?'<button class="epic-more" type="button">Read more</button>':'')+
      '<div class="epic-foot">'+GOOGLE_G+'<span>Posted on Google</span></div>'+
    '</div>';
  }

  function bindReadMore(root){
    root.querySelectorAll('.epic-more').forEach(function(btn){
      btn.addEventListener('click',function(){
        var p=btn.previousElementSibling;
        var expanded=p.classList.toggle('expanded');
        btn.textContent=expanded?'Show less':'Read more';
      });
    });
  }

  function headerHtml(label, ov, writeUrl){
    if(!config.showHeader) return '';
    var btn = (config.showWriteReview && writeUrl) ? '<a class="epic-write" href="'+writeUrl+'" target="_blank" rel="noopener">Write a Review</a>' : '';
    return '<div class="epic-header"><div><div class="epic-h-label">'+label+'</div><div class="epic-h-rating"><span class="epic-h-num">'+(ov.avg_rating||0)+'</span>'+stars(Math.round(ov.avg_rating||0))+'<span class="epic-h-count">('+(ov.total||0)+')</span></div></div>'+btn+'</div>';
  }

  function renderGrid(reviews, label, ov, writeUrl){
    var html=headerHtml(label, ov, writeUrl);
    html+='<div class="epic-wall '+config.layout+'">';
    reviews.forEach(function(r){html+=cardHtml(r);});
    html+='</div>';
    return html;
  }

  function renderCarousel(reviews, label, ov, writeUrl){
    var html=headerHtml(label, ov, writeUrl);
    html+='<div class="epic-carousel">';
    reviews.forEach(function(r){html+=cardHtml(r);});
    html+='</div>';
    return html;
  }

  function renderMarquee(reviews){
    var perRow=Math.ceil(reviews.length/config.rows);
    var html='<div class="epic-marquee">';
    for(var i=0;i<config.rows;i++){
      var row=reviews.slice(i*perRow,(i+1)*perRow);
      var dup=row.concat(row);
      html+='<div class="epic-marquee-row '+(i%2?'reverse':'')+'">';
      dup.forEach(function(r){html+=cardHtml(r);});
      html+='</div>';
    }
    html+='</div>';
    return html;
  }

  // outletsScope is the comma list (or 'all') from the embed config.
  // For "all" we pass nothing; for a specific subset we pass outlet_ids=...
  function scopeParams(){
    if(!config.outlets || config.outlets === 'all') return '';
    return '&outlet_ids='+encodeURIComponent(config.outlets);
  }
  function fetchReviews(outletId, cb){
    // outletId === 'all' means "all selected outlets" (respect scope)
    var url = '${baseUrl}?limit='+config.limit;
    if(outletId && outletId !== 'all'){
      url += '&outlet_id='+encodeURIComponent(outletId);
    } else {
      url += scopeParams();
    }
    fetch(url)
      .then(function(r){return r.json();}).then(function(d){cb(d.reviews||[],d.overall||{avg_rating:0,total:0});});
  }

  function renderByLayout(reviews, label, ov, writeUrl){
    if(config.layout==='marquee'){return renderMarquee(reviews);}
    if(config.layout==='carousel'){return renderCarousel(reviews,label,ov,writeUrl);}
    return renderGrid(reviews,label,ov,writeUrl);
  }

  function renderSingle(){
    // In single mode, scope is the configured outlets (one or more).
    // Pass 'all' to fetchReviews so it falls back to scopeParams (handles 1 or many).
    var scope = (config.outlets && config.outlets.indexOf(',') === -1 && config.outlets !== 'all')
      ? config.outlets : 'all';
    fetchReviews(scope, function(reviews, ov){
      var label = (config.outlets==='all') ? 'Overall Rating' : 'Google Reviews';
      container.innerHTML=renderByLayout(reviews,label,ov,'');
      bindReadMore(container);
    });
  }

  function renderMultiTab(){
    var url='${baseUrl}?limit='+config.limit+'&include_outlets=true'+scopeParams();
    fetch(url)
      .then(function(r){return r.json();}).then(function(d){
        var allOutlets=d.outlets||[]; var ov=d.overall||{avg_rating:0,total:0};
        var isAllScope = !config.outlets || config.outlets === 'all';
        var initialId = 'all';
        var tabs='<div class="epic-tabs">';
        // Always show an "All" tab summarising the scope (single, subset, or every outlet)
        var allLabel = isAllScope ? 'All Reviews' : 'All Selected';
        tabs+='<button class="epic-tab active" data-id="all" data-write="">'+GOOGLE_G+'<span>'+allLabel+'</span><span class="epic-tab-rating">'+(ov.avg_rating||0)+'★</span></button>';
        allOutlets.forEach(function(o){
          tabs+='<button class="epic-tab" data-id="'+o.id+'" data-write="'+(o.google_maps_url||'')+'">'+GOOGLE_G+'<span>'+escapeHtml(o.outlet_name)+'</span><span class="epic-tab-rating">'+(o.avg_rating||0)+'★</span></button>';
        });
        tabs+='</div>';
        container.innerHTML=tabs+'<div class="epic-tab-body"></div>';
        var body=container.querySelector('.epic-tab-body');
        function fetchAndRender(outletId, writeUrl){
          fetchReviews(outletId,function(reviews,ovv){
            body.innerHTML=renderByLayout(reviews, outletId==='all'?'Overall Rating':'Google Reviews', ovv, writeUrl);
            bindReadMore(body);
          });
        }
        function setActiveTab(el){
          container.querySelectorAll('.epic-tab').forEach(function(x){x.classList.remove('active');});
          el.classList.add('active');
        }
        fetchAndRender(initialId, '');
        container.querySelectorAll('.epic-tab').forEach(function(t){
          t.addEventListener('click',function(){
            setActiveTab(t);
            fetchAndRender(t.dataset.id, t.dataset.write||'');
          });
        });
      });
  }

  if(config.mode==='multi-tab'){renderMultiTab();} else {renderSingle();}
})();
</script>
<style>
#epic-walloflove{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#111827}
#epic-walloflove .epic-header{display:flex;align-items:center;justify-content:space-between;padding:12px 4px 16px}
#epic-walloflove .epic-h-label{font-size:13px;color:#6B7280;margin-bottom:4px}
#epic-walloflove .epic-h-rating{display:flex;align-items:center;gap:8px}
#epic-walloflove .epic-h-num{font-size:28px;font-weight:700;color:#111827}
#epic-walloflove .epic-h-count{color:#6B7280;font-size:14px}
#epic-walloflove .epic-write{background:#1A73E8;color:#fff;padding:8px 16px;border-radius:8px;text-decoration:none;font-size:14px;font-weight:500}
#epic-walloflove .epic-wall{display:grid;gap:16px}
#epic-walloflove .epic-wall.masonry{display:block;columns:3;column-gap:16px;grid-template-columns:none}
#epic-walloflove .epic-wall.masonry .epic-card{break-inside:avoid;page-break-inside:avoid;margin:0 0 16px 0;display:inline-block;width:100%;min-width:0}
@media (max-width:1024px){#epic-walloflove .epic-wall.masonry{columns:2}}
@media (max-width:640px){#epic-walloflove .epic-wall.masonry{columns:1}}
#epic-walloflove .epic-carousel{display:flex;gap:16px;overflow-x:auto;padding:8px 4px 16px;scroll-snap-type:x mandatory}
#epic-walloflove .epic-carousel .epic-card{scroll-snap-align:start}
#epic-walloflove .epic-marquee{display:flex;flex-direction:column;gap:16px;overflow:hidden;padding:8px 0}
#epic-walloflove .epic-marquee-row{display:flex;gap:16px;animation:epic-mq-l 40s linear infinite;width:max-content}
#epic-walloflove .epic-marquee-row.reverse{animation-name:epic-mq-r}
@keyframes epic-mq-l{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes epic-mq-r{from{transform:translateX(-50%)}to{transform:translateX(0)}}
#epic-walloflove .epic-card{background:#fff;border:1px solid #F3F4F6;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,.06);padding:16px;width:280px;min-width:280px;display:flex;flex-direction:column;gap:10px;flex-shrink:0}
#epic-walloflove .epic-head{display:flex;gap:12px;align-items:flex-start}
#epic-walloflove .epic-ava-wrap{position:relative}
#epic-walloflove .epic-g-badge{position:absolute;bottom:-2px;left:-2px;background:#fff;border-radius:50%;padding:2px;box-shadow:0 1px 2px rgba(0,0,0,.15);width:16px;height:16px;display:flex;align-items:center;justify-content:center}
#epic-walloflove .epic-g-badge svg{width:10px;height:10px}
#epic-walloflove .epic-name{font-weight:600;font-size:14px;display:flex;align-items:center;gap:4px}
#epic-walloflove .epic-time{font-size:12px;color:#6B7280}
#epic-walloflove .epic-body{font-size:14px;line-height:1.5;color:#374151;margin:0;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
#epic-walloflove .epic-body.expanded{display:block;-webkit-line-clamp:unset;overflow:visible}
#epic-walloflove .epic-more{background:none;border:none;color:#1A73E8;font-size:12px;cursor:pointer;padding:0;text-align:left}
#epic-walloflove .epic-foot{display:flex;align-items:center;gap:6px;font-size:11px;color:#9CA3AF;margin-top:auto}
#epic-walloflove .epic-tabs{display:flex;gap:8px;overflow-x:auto;border-bottom:1px solid #E5E7EB;padding-bottom:0}
#epic-walloflove .epic-tab{display:flex;align-items:center;gap:6px;background:none;border:none;padding:12px 16px;cursor:pointer;font-size:14px;color:#6B7280;border-bottom:2px solid transparent;white-space:nowrap}
#epic-walloflove .epic-tab.active{color:#1A73E8;border-bottom-color:#1A73E8;font-weight:600}
#epic-walloflove .epic-tab-rating{font-size:12px;color:#6B7280}
#epic-walloflove .epic-tab.active .epic-tab-rating{color:#1A73E8}
@media(max-width:600px){#epic-walloflove .epic-header{flex-direction:column;align-items:flex-start;gap:12px}}
</style>`;

    setEmbedCode(code);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast({ title: "Copied!", description: "Embed code copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveSettings = async () => {
    try {
      // Save the first selected outlet (or null) to the legacy single-id column.
      const primaryOutletId = isAllOutlets ? null : selectedIds[0] || null;
      const { error } = await supabase.from("widget_settings").upsert({
        outlet_id: primaryOutletId,
        layout,
        theme,
        max_reviews: maxReviews[0],
        card_style: cardStyle,
        last_generated_at: new Date().toISOString(),
      });
      if (error) throw error;
      toast({ title: "Settings saved", description: "Widget settings saved successfully" });
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    }
  };

  // Preview helpers — in multi-tab mode the active tab drives the header
  const isMultiTab = displayMode === "multi-tab";
  const headerLabel =
    (!isMultiTab && isAllOutlets) || (isMultiTab && activeTab === "all")
      ? "Overall Rating"
      : "Google Reviews";

  const activeOutletObj = outletSummaries.find((o) => o.id === activeTab);
  const activeOverall = isMultiTab && activeTab !== "all" && activeOutletObj
    ? { avg_rating: activeOutletObj.avg_rating, total: activeOutletObj.total }
    : overall;
  const activeWriteUrl = isMultiTab && activeTab !== "all"
    ? activeOutletObj?.google_maps_url || ""
    : writeReviewUrl;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Widget Generator</CardTitle>
        <CardDescription>Customize and generate embed code for your Wall of Love</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Display Mode Toggle */}
        <div className="space-y-2">
          <Label>Display Mode</Label>
          <div className="inline-flex rounded-lg border bg-muted p-1">
            <button
              type="button"
              onClick={() => setDisplayMode("single")}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                displayMode === "single"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground"
              }`}
            >
              Single Outlet View
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode("multi-tab")}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                displayMode === "multi-tab"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground"
              }`}
            >
              Multi-Outlet Tabs
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Outlet Filter — multi-select */}
          <div className="space-y-2">
            <Label>Outlet Filter</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between font-normal">
                  <span className="truncate text-left">
                    {isAllOutlets
                      ? "All Outlets"
                      : selectedIds.length === 1
                      ? outlets.find((o) => o.id === selectedIds[0])?.outlet_name || "1 outlet"
                      : `${selectedIds.length} outlets selected`}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <div className="max-h-[300px] overflow-y-auto py-1">
                  <button
                    type="button"
                    onClick={() => setOutletFilters(["all"])}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
                  >
                    <Checkbox checked={isAllOutlets} className="pointer-events-none" />
                    <span className="font-medium">All Outlets</span>
                  </button>
                  <div className="my-1 h-px bg-border" />
                  {outlets.map((o) => {
                    const checked = !isAllOutlets && selectedIds.includes(o.id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => {
                          if (isAllOutlets) {
                            setOutletFilters([o.id]);
                            return;
                          }
                          if (checked) {
                            const next = selectedIds.filter((x) => x !== o.id);
                            setOutletFilters(next.length === 0 ? ["all"] : next);
                          } else {
                            setOutletFilters([...selectedIds, o.id]);
                          }
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
                      >
                        <Checkbox checked={checked} className="pointer-events-none" />
                        <span className="truncate">{o.outlet_name}</span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Layout */}
          <div className="space-y-2">
            <Label>Layout</Label>
            <Select value={layout} onValueChange={setLayout}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="masonry">Masonry Grid</SelectItem>
                <SelectItem value="carousel">Carousel</SelectItem>
                <SelectItem value="marquee">Marquee</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Theme */}
          <div className="space-y-2">
            <Label>Theme</Label>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="auto">Auto</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Card Style */}
          <div className="space-y-2">
            <Label>Card Style</Label>
            <Select value={cardStyle} onValueChange={setCardStyle}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rounded">Rounded</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
                <SelectItem value="bordered">Bordered</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Max Reviews */}
          <div className="space-y-2">
            <Label>Max Reviews: {maxReviews[0]}</Label>
            <Slider value={maxReviews} onValueChange={setMaxReviews} min={1} max={20} step={1} />
          </div>

          {/* Rows — Masonry & Marquee */}
          {(layout === "masonry" || layout === "marquee") && (
            <div className="space-y-2">
              <Label>Rows: {rows[0]}</Label>
              <Slider
                value={rows}
                onValueChange={setRows}
                min={layout === "marquee" ? 2 : 1}
                max={6}
                step={1}
              />
            </div>
          )}

          <div className="flex items-center justify-between space-y-0">
            <Label htmlFor="show-header">Show Overall Rating Header</Label>
            <Switch id="show-header" checked={showHeader} onCheckedChange={setShowHeader} />
          </div>

          <div className="flex items-center justify-between space-y-0">
            <Label htmlFor="show-write">Show "Write a Review" Button</Label>
            <Switch id="show-write" checked={showWriteReview} onCheckedChange={setShowWriteReview} />
          </div>
        </div>

        {/* Embed Code */}
        <div className="space-y-2">
          <Label>Embed Code</Label>
          <div className="relative">
            <Textarea value={embedCode} readOnly className="font-mono text-xs h-64" />
            <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Widget Preview */}
        <div className="space-y-2">
          <Label>Widget Preview</Label>
          <Card className={theme === "dark" ? "bg-gray-950 border-dashed" : "bg-gray-50 border-dashed"}>
            <CardContent className="p-6">
              {previewReviews.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Add your first review to Wall of Love to preview
                </div>
              ) : (
                <>
                  {/* Multi-tab preview */}
                  {isMultiTab && (
                    <div className="mb-4 border-b border-gray-200 flex gap-1 overflow-x-auto">
                      <button
                        onClick={() => setActiveTab("all")}
                        className={`flex items-center gap-1.5 px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px ${
                          activeTab === "all"
                            ? "border-blue-600 text-blue-600 font-semibold"
                            : "border-transparent text-gray-500"
                        }`}
                      >
                        <GoogleG size={14} /> All Reviews
                        <span className="text-xs">{overall.avg_rating}★</span>
                      </button>
                      {outletSummaries.map((o) => (
                        <button
                          key={o.id}
                          onClick={() => setActiveTab(o.id)}
                          className={`flex items-center gap-1.5 px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px ${
                            activeTab === o.id
                              ? "border-blue-600 text-blue-600 font-semibold"
                              : "border-transparent text-gray-500"
                          }`}
                        >
                          <GoogleG size={14} /> {o.outlet_name}
                          <span className="text-xs">{o.avg_rating}★</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Header */}
                  {showHeader && effectiveLayout !== "marquee" && (
                    <div className="flex items-center justify-between mb-4 pb-2">
                      <div>
                        <div className={`text-xs mb-1 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{headerLabel}</div>
                        <div className="flex items-center gap-2">
                          <span className={`text-2xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                            {activeOverall.avg_rating}
                          </span>
                          <Stars rating={Math.round(activeOverall.avg_rating)} size={16} />
                          <span className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>({activeOverall.total})</span>
                        </div>
                      </div>
                      {showWriteReview && activeWriteUrl && (
                        <a
                          href={activeWriteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg font-medium"
                        >
                          Write a Review
                        </a>
                      )}
                    </div>
                  )}

                  {/* Cards */}
                  {(() => {
                    const limited = previewReviews.slice(0, maxReviews[0]);
                    if (effectiveLayout === "masonry") {
                      return (
                        <div className="epic-preview-masonry">
                          {limited.map((r) => (
                            <div key={r.id} className="epic-preview-masonry-item">
                              <PreviewCard review={r} cardStyle={cardStyle} theme={theme} fullWidth />
                            </div>
                          ))}
                          <style>{`.epic-preview-masonry{columns:3;column-gap:16px}.epic-preview-masonry-item{break-inside:avoid;page-break-inside:avoid;margin-bottom:16px;display:inline-block;width:100%}@media (max-width:1024px){.epic-preview-masonry{columns:2}}@media (max-width:640px){.epic-preview-masonry{columns:1}}`}</style>
                        </div>
                      );
                    }
                    if (effectiveLayout === "marquee") {
                      const rowCount = Math.max(rows[0], 1);
                      const perRow = Math.ceil(limited.length / rowCount) || 1;
                      const groups = Array.from({ length: rowCount }, (_, i) =>
                        limited.slice(i * perRow, (i + 1) * perRow)
                      );
                      return (
                        <div className="flex flex-col gap-4 overflow-hidden">
                          {groups.map((g, i) => (
                            <div key={i} className="flex gap-4 overflow-hidden">
                              <div className={`flex gap-4 shrink-0 ${i % 2 ? "animate-[marquee-r_40s_linear_infinite]" : "animate-[marquee-l_40s_linear_infinite]"}`}>
                                {g.concat(g).map((r, j) => (
                                  <PreviewCard key={`${r.id}-${j}`} review={r} cardStyle={cardStyle} theme={theme} />
                                ))}
                              </div>
                            </div>
                          ))}
                          <style>{`@keyframes marquee-l{from{transform:translateX(0)}to{transform:translateX(-50%)}}@keyframes marquee-r{from{transform:translateX(-50%)}to{transform:translateX(0)}}`}</style>
                        </div>
                      );
                    }
                    // carousel (and multi-tab body)
                    return (
                      <div className="flex gap-4 overflow-x-auto pb-2">
                        {limited.map((r) => (
                          <PreviewCard key={r.id} review={r} cardStyle={cardStyle} theme={theme} />
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSaveSettings}>Save Settings</Button>
          <Button variant="outline" onClick={generateEmbedCode}>
            Regenerate Code
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
